import { Link, useNavigate } from "react-router-dom"
import DashboardSideBar from "../../components/dashboardSidebar"
import Footer from "../../components/footer"
import Header from "../../components/header"
import SideBar from "../../components/sidebar"
import { useState, useEffect, useRef } from "react"
import services from "../../services"
import Toaster from "../../components/toaster";
import useIsMobile from "../../hooks/useIsMobile";
import MobileHeader from "../../components/MobileHeader";
import getAvailabilityBlocker from "./availabilityBlocker";
import CitySelect from "../../components/CitySelect";

const AddOneWayAvailability = () => {
    let navigate = useNavigate();
    const isMobileView = useIsMobile();
    const [availabilityData, setAvailabilityData] = useState({
        vendorCarsId: '',
        from: {
            longitude: '',
            latitude: '',
            full_address: ''
        },
        to: {
            longitude: '',
            latitude: '',
            full_address: ''
        },
        tripDate: '',
        contactNumber: '',
        alternativeContactNumber: '',
        isActive: true
    })
    const [toaster, setToaster] = useState({ visiblity: "hide" });
    const [loading, setLoading] = useState(false)
    const [carsList, setCarsList] = useState([])
    const [carsLoading, setCarsLoading] = useState(true)
    const [citiesList, setCitiesList] = useState([])
    const [maxTripDate, setMaxTripDate] = useState(null)
    // Trips are dated only (no time); can't be in the past
    const todayStr = new Date().toLocaleDateString('en-CA')
    const [tripDateError, setTripDateError] = useState('')
    // Reason the vendor cannot add availability (account status, subscription or no car)
    const [blocker, setBlocker] = useState(null)
    
    // Location search states
    const [fromSearchTerm, setFromSearchTerm] = useState('')
    const [toSearchTerm, setToSearchTerm] = useState('')
    const [fromSuggestions, setFromSuggestions] = useState([])
    const [toSuggestions, setToSuggestions] = useState([])
    const [showFromSuggestions, setShowFromSuggestions] = useState(false)
    const [showToSuggestions, setShowToSuggestions] = useState(false)
    const fromSearchTimeoutRef = useRef(null)
    const toSearchTimeoutRef = useRef(null)
    const fromDropdownRef = useRef(null)
    const toDropdownRef = useRef(null)

    const handleCityFrom = (cid) => {
        const c = (citiesList || []).find((x) => String(x.id) === String(cid));
        setAvailabilityData((prev) => ({
            ...prev,
            from: c
                ? { longitude: c.longitude, latitude: c.latitude, full_address: c.name, cityId: c.id }
                : { longitude: '', latitude: '', full_address: '' }
        }));
    };
    useEffect(() => {
        services.Cars.GET_CITIES().then((r) => setCitiesList(r.data || [])).catch(() => {});
    }, []);
    // Fetch vendor cars and subscriptions
    useEffect(() => {
        const fetchCars = async () => {
            setCarsLoading(true)
            try {
                const response = await services.Cars.GET_VENDOR_CAR();
                setCarsList(response.data || [])
            } catch (error) {
                console.log(error)
                setToaster({
                    type: "danger",
                    title: "Error Occured",
                    text: error.response?.data?.message || "Failed to fetch cars",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
            } finally {
                setCarsLoading(false)
            }
        }

        const fetchSubscriptions = async () => {
            try {
                const response = await services.Vendor.GET_ALL_VENDOR_SUBSCRIPTIONS();
                const subscriptions = response.data || [];
                const activeSubscriptions = subscriptions.filter(sub => sub.isActive === true);
                
                if (activeSubscriptions.length > 0) {
                    // Find the latest end date from active subscriptions
                    // Extract date part directly from ISO string to avoid timezone issues
                    const endDateStrings = activeSubscriptions.map(sub => {
                        // Extract YYYY-MM-DD from ISO string (handles both "2026-01-15T13:56:00.000Z" and date-only formats)
                        const dateStr = sub.endDate.split('T')[0];
                        return dateStr;
                    });
                    
                    // Sort and get the latest date string
                    endDateStrings.sort();
                    const latestEndDateStr = endDateStrings[endDateStrings.length - 1];
                    
                    // Set max trip date to the latest end date (format: YYYY-MM-DD)
                    setMaxTripDate(latestEndDateStr);
                } else {
                    setMaxTripDate(null);
                }
            } catch (error) {
                console.log(error)
                // Don't show error for subscription fetch failure, just log it
            }
        }

        const checkEligibility = async () => {
            try {
                const { data } = await services.Vendor.GET_ONE_WAY_ELIGIBILITY();
                setBlocker(getAvailabilityBlocker(data))
            } catch (error) {
                console.log(error)
            }
        }

        fetchCars()
        fetchSubscriptions()
        checkEligibility()
    }, [])

    // Handle from location search
    const handleFromSearch = async (searchKey) => {
        if (!searchKey || searchKey.length < 2) {
            setFromSuggestions([])
            setShowFromSuggestions(false)
            return
        }

        try {
            const response = await services.Location.GET_LOCATION(searchKey);
            if (response.data?.features) {
                setFromSuggestions(response.data.features)
                setShowFromSuggestions(true)
            }
        } catch (error) {
            console.log(error)
            setFromSuggestions([])
            setShowFromSuggestions(false)
        }
    }

    // Handle to location search
    const handleToSearch = async (searchKey) => {
        if (!searchKey || searchKey.length < 2) {
            setToSuggestions([])
            setShowToSuggestions(false)
            return
        }

        try {
            const response = await services.Location.GET_LOCATION(searchKey);
            if (response.data?.features) {
                setToSuggestions(response.data.features)
                setShowToSuggestions(true)
            }
        } catch (error) {
            console.log(error)
            setToSuggestions([])
            setShowToSuggestions(false)
        }
    }

    // Handle from input change with debounce
    const handleFromInputChange = (value) => {
        setFromSearchTerm(value)
        if (fromSearchTimeoutRef.current) {
            clearTimeout(fromSearchTimeoutRef.current)
        }
        fromSearchTimeoutRef.current = setTimeout(() => {
            handleFromSearch(value)
        }, 300)
    }

    // Handle to input change with debounce
    const handleToInputChange = (value) => {
        setToSearchTerm(value)
        if (toSearchTimeoutRef.current) {
            clearTimeout(toSearchTimeoutRef.current)
        }
        toSearchTimeoutRef.current = setTimeout(() => {
            handleToSearch(value)
        }, 300)
    }

    // Handle from location selection
    const handleFromSelect = (feature) => {
        setAvailabilityData(prev => ({
            ...prev,
            from: {
                longitude: feature.properties.coordinates.longitude,
                latitude: feature.properties.coordinates.latitude,
                full_address: feature.properties.full_address
            }
        }))
        setFromSearchTerm(feature.properties.full_address)
        setShowFromSuggestions(false)
    }

    // Handle to location selection
    const handleToSelect = (feature) => {
        setAvailabilityData(prev => ({
            ...prev,
            to: {
                longitude: feature.properties.coordinates.longitude,
                latitude: feature.properties.coordinates.latitude,
                full_address: feature.properties.full_address
            }
        }))
        setToSearchTerm(feature.properties.full_address)
        setShowToSuggestions(false)
    }

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
                setShowFromSuggestions(false)
            }
            if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
                setShowToSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Set search terms when data is loaded
    useEffect(() => {
        if (availabilityData.from.full_address) {
            setFromSearchTerm(availabilityData.from.full_address)
        }
        if (availabilityData.to.full_address) {
            setToSearchTerm(availabilityData.to.full_address)
        }
    }, [])

    const HandleOnChangeValue = (value, key) => {
        // Validate trip date when it changes
        if (key === 'tripDate' && value && maxTripDate) {
            // Extract date part only (YYYY-MM-DD) for comparison
            const selectedDateStr = value.split('T')[0] || value;
            const selectedDate = new Date(selectedDateStr);
            selectedDate.setHours(0, 0, 0, 0);
            
            const maxDate = new Date(maxTripDate);
            maxDate.setHours(0, 0, 0, 0);
            
            // Compare dates only (allowing same day - subscription end date is included)
            if (selectedDate > maxDate) {
                const formattedDate = new Date(maxTripDate);
                setTripDateError(`Trip date must be on or before ${formattedDate.toLocaleDateString()} (subscription end date is included)`);
            } else {
                setTripDateError('');
            }
        } else if (key === 'tripDate' && !value) {
            setTripDateError('');
        }

        setAvailabilityData({
            ...availabilityData,
            [key]: value
        })
    }

    const onSubmitHandler = async () => {
        // Validation
        if (!availabilityData.vendorCarsId) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "Car is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }
        if (!availabilityData.from.longitude || !availabilityData.from.latitude || !availabilityData.from.full_address) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "From location is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }
        if (!availabilityData.to.longitude || !availabilityData.to.latitude || !availabilityData.to.full_address) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "To location is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }
        if (!availabilityData.tripDate) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "Trip Date is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }

        // Validate trip date against subscription end date
        if (maxTripDate && availabilityData.tripDate) {
            // Extract date part only (YYYY-MM-DD) for comparison
            const selectedDateStr = availabilityData.tripDate.split('T')[0] || availabilityData.tripDate;
            const selectedDate = new Date(selectedDateStr);
            selectedDate.setHours(0, 0, 0, 0);
            
            const maxDate = new Date(maxTripDate);
            maxDate.setHours(0, 0, 0, 0);
            
            if (selectedDate > maxDate) {
                const formattedDate = new Date(maxTripDate);
                setToaster({
                    type: "danger",
                    title: "Validation Error",
                    text: `Trip date must be on or before ${formattedDate.toLocaleDateString()} (subscription end date is included)`,
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 3000)
                return;
            }
        }
        if (!availabilityData.contactNumber) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "Contact Number is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }

        // Convert tripDate to ISO format
        const payload = {
            ...availabilityData,
            vendorCarsId: parseInt(availabilityData.vendorCarsId),
            tripDate: new Date(availabilityData.tripDate).toISOString(),
            alternativeContactNumber: availabilityData.alternativeContactNumber || null
        }

        setLoading(true)
        try {
            const response = await services.Vendor.CREATE_ONE_WAY_AVAILABILITY(payload);
            console.log(response)
            setToaster({
                type: "success",
                title: "Availability Added",
                text: 'Availability added successfully',
                visiblity: "show",
            });
            setTimeout(() => {
                navigate('/oneWayAvailabilities')
            }, 300)
        } catch (error) {
            console.log(error)
            setToaster({
                type: "danger",
                title: "Error Occured",
                text: error.response?.data?.message || "Failed to add availability",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({
                    visiblity: "hide",
                });
            }, 1500)
        } finally {
            setLoading(false)
        }
    }

    const blockerModal = blocker && (
        <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
            tabIndex="-1"
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{blocker.title}</h5>
                    </div>
                    <div className="modal-body">
                        <p>{blocker.text}</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => navigate('/oneWayAvailabilities')}>Back</button>
                        {blocker.actionTo && (
                            <button type="button" className="btn theme-btn" onClick={() => navigate(blocker.actionTo)}>{blocker.actionLabel}</button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )

    if (isMobileView) {
        return (
            <>
                <Toaster type={toaster.type} title={toaster.title} text={toaster.text} visiblity={toaster.visiblity} />
                <SideBar />
                <main className="mlux mlux-auth">
                    <MobileHeader back="/oneWayAvailabilities" />
                    <div className="mlux-greet" style={{ margin: '8px 4px 2px' }} data-rise="1">
                        <div className="kick">Empty-return trip</div>
                        <div className="nm" style={{ fontSize: 22 }}>Add Availability</div>
                    </div>
                    <div className="mlux-card" style={{ padding: 16, marginTop: 12 }} data-rise="2">
                        <label className="mlux-lbl">Select car</label>
                        <div className="mlux-field no-icon select"><select value={availabilityData.vendorCarsId || ''} onChange={(e) => HandleOnChangeValue(e.target.value, 'vendorCarsId')}><option value="">Select car</option>{carsList.map((car) => <option key={car.id} value={car.id}>{car.Car?.name || car.car?.name} ({car.Car?.brand || car.car?.brand}) - {car.rcNumber}</option>)}</select></div>

                        <label className="mlux-lbl">Trip date</label>
                        <div className="mlux-field"><i className="far fa-calendar"></i><input type="date" value={availabilityData.tripDate || ''} min={todayStr} max={maxTripDate || undefined} onChange={(e) => HandleOnChangeValue(e.target.value, 'tripDate')} /></div>

                        <label className="mlux-lbl">From city</label>
                        <CitySelect variant="mobile" icon="far fa-location-dot" cities={citiesList} value={availabilityData.from?.cityId} onChange={handleCityFrom} />

                        <label className="mlux-lbl">To location</label>
                        <div className="mlux-field"><i className="fas fa-flag-checkered"></i><input type="text" value={toSearchTerm} placeholder="Search to…" onChange={(e) => handleToInputChange(e.target.value)} /></div>
                        {showToSuggestions && toSuggestions.length > 0 && <ul className="mlux-suggest">{toSuggestions.map((f, i) => <li key={i} onClick={() => handleToSelect(f)}><i className="far fa-location-dot"></i>{f.properties.full_address}</li>)}</ul>}

                        <label className="mlux-lbl">Contact number</label>
                        <div className="mlux-field no-icon"><input type="number" value={availabilityData.contactNumber} placeholder="Phone" onChange={(e) => HandleOnChangeValue(e.target.value, 'contactNumber')} /></div>
                        <label className="mlux-lbl">Alternative contact (optional)</label>
                        <div className="mlux-field no-icon"><input type="number" value={availabilityData.alternativeContactNumber} placeholder="Alternative phone" onChange={(e) => HandleOnChangeValue(e.target.value, 'alternativeContactNumber')} /></div>

                        <button className="mlux-btn" style={{ marginTop: 12 }} disabled={loading} onClick={onSubmitHandler}><i className="far fa-circle-check"></i> {loading ? 'Saving…' : 'Save availability'}</button>
                    </div>
                </main>
                {blockerModal}
            </>
        );
    }

    return (<>
        <Header />
        <SideBar />
        <main class="main">

            <Toaster
                type={toaster.type}
                title={toaster.title}
                text={toaster.text}
                visiblity={toaster.visiblity}
            />

            <div class="user-profile py-5">
                <div class="container">
                    <div class="row">
                        <DashboardSideBar />
                        <div class="col-lg-9">
                            <div class="user-profile-wrapper">
                                <div class="row">
                                    <div class="col-lg-12">
                                        <div class="user-profile-card">
                                            <div class="user-profile-card-header">
                                                <h4 class="user-profile-card-title">Add One-Way Availability</h4>
                                            </div>
                                            <div class="user-profile-form">

                                                <div class="row">
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Car <span class="text-danger">*</span></label>
                                                            <select
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'vendorCarsId')}
                                                                class="form-control"
                                                                value={availabilityData.vendorCarsId}
                                                                required
                                                                disabled={carsLoading}
                                                            >
                                                                <option value="">Select Car</option>
                                                                {carsList.map((car) => (
                                                                    <option key={car.id} value={car.id}>
                                                                        {car.Car?.name || car.car?.name || 'N/A'} ({car.Car?.brand || car.car?.brand || 'N/A'}) - {car.rcNumber || 'N/A'}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!availabilityData.vendorCarsId && <small className="text-danger">Car is required</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Trip Date <span class="text-danger">*</span></label>
                                                            <input
                                                                type="date"
                                                                min={todayStr}
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'tripDate')}
                                                                class="form-control"
                                                                value={availabilityData.tripDate}
                                                                placeholder="Trip Date"
                                                                max={maxTripDate || undefined}
                                                                required
                                                            />
                                                            {!availabilityData.tripDate && <small className="text-danger">Trip Date is required</small>}
                                                            {tripDateError && <small className="text-danger" style={{ display: 'block', marginTop: '5px' }}>{tripDateError}</small>}
                                                            {maxTripDate && !tripDateError && (
                                                                <small className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                                                                    Maximum date: {new Date(maxTripDate).toLocaleDateString()} (subscription end date is included)
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>From City <span class="text-danger">*</span></label>
                                                            <CitySelect cities={citiesList} value={availabilityData.from?.cityId} onChange={handleCityFrom} />
                                                            {(!availabilityData.from.full_address) && <small className="text-danger">From city is required</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group" ref={toDropdownRef} style={{ position: 'relative' }}>
                                                            <label>To Location <span class="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                onChange={(e) => handleToInputChange(e.target.value)}
                                                                class="form-control"
                                                                value={toSearchTerm}
                                                                placeholder="Search to location..."
                                                                required
                                                            />
                                                            {showToSuggestions && toSuggestions.length > 0 && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    backgroundColor: 'white',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px',
                                                                    maxHeight: '200px',
                                                                    overflowY: 'auto',
                                                                    zIndex: 1000,
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                                }}>
                                                                    {toSuggestions.map((feature, index) => (
                                                                        <div
                                                                            key={index}
                                                                            onClick={() => handleToSelect(feature)}
                                                                            style={{
                                                                                padding: '10px',
                                                                                cursor: 'pointer',
                                                                                borderBottom: '1px solid #eee'
                                                                            }}
                                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                                        >
                                                                            {feature.properties.full_address}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {(!availabilityData.to.full_address) && <small className="text-danger">To location is required</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Contact Number <span class="text-danger">*</span></label>
                                                            <input
                                                                type="tel"
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'contactNumber')}
                                                                class="form-control"
                                                                value={availabilityData.contactNumber}
                                                                placeholder="Contact Number"
                                                                required
                                                            />
                                                            {!availabilityData.contactNumber && <small className="text-danger">Contact Number is required</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Alternative Contact Number</label>
                                                            <input
                                                                type="tel"
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'alternativeContactNumber')}
                                                                class="form-control"
                                                                value={availabilityData.alternativeContactNumber}
                                                                placeholder="Alternative Contact Number (Optional)"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Status</label>
                                                            <select
                                                                onChange={(e) => HandleOnChangeValue(e.target.value === 'true', 'isActive')}
                                                                class="form-control"
                                                                value={availabilityData.isActive ? 'true' : 'false'}
                                                            >
                                                                <option value="true">Active</option>
                                                                <option value="false">Inactive</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="d-flex align-items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={onSubmitHandler}
                                                        class="theme-btn my-3"
                                                        disabled={loading}
                                                    >
                                                        <span class="far fa-save"></span> {loading ? 'Saving...' : 'Save Availability'}
                                                    </button>
                                                    <Link to={'/oneWayAvailabilities'} class="theme-btn my-3" style={{ background: '#6c757d' }}>
                                                        <span class="far fa-times"></span> Cancel
                                                    </Link>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </main>
        <Footer />
        {blockerModal}
    </>
    )
}
export default AddOneWayAvailability

